import { BadgeCheck, Bookmark, Star, X } from 'lucide-react';
import type { ReactNode } from 'react';

import type { LibraryItem, UserShowStatus } from '@shared/types';

import { cn } from '@/lib/utils';
import { progressPercent } from '@/lib/library';

/**
 * The status-indication marks (screens.md §`/` overlay table — identical
 * meaning to design.md §6, restyled for the web). The marks are decorative
 * (aria-hidden); the status is announced by the tile's accessible label.
 *
 * | Status    | Mark                                        | Colour  |
 * | Watching  | thin progress bar along the bottom edge     | on-image (white on a scrim) — not amber: a wall of in-progress covers would put amber on every tile |
 * | Completed | small BadgeCheck seal, top-right            | success |
 * | Watchlist | Bookmark, top-right                         | ink     |
 * | Dropped   | dimmed poster + small X, top-right          | danger  |
 * | Rated     | Star + score chip, top-left                 | rating  |
 *
 * They compose: a completed + rated title shows both.
 *
 * Optional, for surfaces that say these things elsewhere (the Library wall):
 * `rating={false}` drops the rated chip; `omit` drops the marks (and the
 * dropped dim) of statuses the active filter already implies.
 */
export function StatusOverlay({
  item,
  rating = true,
  omit = [],
}: {
  item: LibraryItem;
  rating?: boolean;
  omit?: readonly UserShowStatus[];
}) {
  const { userRating } = item;
  const status = item.status === 'removed' || omit.includes(item.status) ? null : item.status;
  const showRating = rating && item.status !== 'removed' && userRating != null && userRating > 0;
  if (!status && !showRating) return null;

  const pct = Math.min(100, Math.round(progressPercent(item)));

  return (
    <div aria-hidden className="pointer-events-none absolute inset-0">
      {status === 'dropped' && <div className="absolute inset-0 bg-overlay" />}

      {showRating && (
        <span className="absolute left-1.5 top-1.5 flex items-center gap-0.5 rounded-full bg-rating py-0.5 pl-1 pr-1.5 text-on-accent">
          <Star className="size-3 fill-current" strokeWidth={1.75} />
          <span className="text-kicker leading-none">{userRating}</span>
        </span>
      )}

      {status === 'completed' && (
        <Mark className="bg-success text-on-image">
          <BadgeCheck className="size-3.5" strokeWidth={1.75} />
        </Mark>
      )}
      {status === 'watchlist' && (
        <Mark className="bg-ink text-bg">
          <Bookmark className="size-3.5 fill-current" strokeWidth={1.75} />
        </Mark>
      )}
      {status === 'dropped' && (
        <Mark className="bg-danger text-on-danger">
          <X className="size-3.5" strokeWidth={2} />
        </Mark>
      )}

      {status === 'watching' && pct > 0 && (
        <span className="absolute inset-x-0 bottom-0 h-1 bg-scrim-strong">
          <span className="block h-full bg-on-image" style={{ width: `${pct}%` }} />
        </span>
      )}
    </div>
  );
}

function Mark({ className, children }: { className?: string; children: ReactNode }) {
  return (
    <span
      className={cn(
        'absolute right-1.5 top-1.5 flex size-6 items-center justify-center rounded-full',
        className,
      )}
    >
      {children}
    </span>
  );
}
